<template>
  <LoadingScreen
    v-if="loading"
    :loading="loading"
    :messages="loadingMessages"
  />
  <div class="content">
    <Logo />
    <!-- Two Column Layout -->
    <div
      style="
        display: flex;
        width: 100%;
        justify-content: space-between;
        align-items: flex-start;
        gap: 20px;
        margin-bottom: 20px;
      "
    >
      <!-- Left Column with Callout -->
      <div style="flex: 1; display: flex; flex-direction: column; margin-top: 60px;">
        <Callout />
      </div>

      <!-- Right Column with Form -->
      <div style="flex: 1">
        <form id="form" class="callout">
          <!-- Advisor Fields -->
          <h1
            style="
              color: #001f5a;
              font-size: 3em;
              font-weight: 400;
              margin-top: 0px;
              margin-bottom: 10px;
            "
          >
            Start a conversation...
          </h1>
          <p><strong>*</strong> indicates a mandatory field</p>
          <p>
            <input
              v-model="form.advisorName"
              :class="{ invalid: !validation.advisorName }"
              @input="clearValidation('advisorName')"
              placeholder="Advisor Name *"
              required
            />
          </p>
          <p>
            <input
              v-model="form.email"
              type="email"
              :class="{ invalid: !validation.email }"
              placeholder="Advisor E-mail *"
              @input="clearValidation('email')"
            />
          </p>
          <p>
            <input
              v-model="form.contactNumber"
              type="tel"
              :class="{ invalid: !validation.contactNumber }"
              @input="clearValidation('contactNumber')"
              placeholder="Advisor Phone Number *"
              required
            />
          </p>
          <p>
            <input
              v-model="form.firm"
              :class="{ invalid: !validation.firm }"
              @input="clearValidation('firm')"
              placeholder="Firm Name *"
              required
            />
          </p>
          <p>
            <input v-model="form.repCode" placeholder="Rep Code" />
          </p>
          <!-- Loan Amount Field -->
          <p>
            <span style="position: relative; display: inline-block">
              <input
                v-model="form.loanAmount"
                type="number"
                :class="{ invalid: !validation.loanAmount }"
                @input="clearValidation('loanAmount')"
                placeholder="Amount *"
                style="padding-left: 25px"
              />
              <span
                style="
                  position: absolute;
                  left: 10px;
                  top: 50%;
                  transform: translateY(-50%);
                "
                >$</span
              >
            </span>
          </p>

          <!-- Custodian Field -->
          <p>
            <input v-model="form.custodian" placeholder="Custodian" />
          </p>

          <p>
            <select v-model="form.state" :class="{ placeholder: !form.state }">
              <option value="" hidden>Borrower's State</option>
              <option v-for="state in states" :value="state">
                {{ state }}
              </option>
            </select>
          </p>

          <!-- Confirmation Field -->
          <p style="display: flex; align-items: center">
            <input
              v-model="contactMeChecked"
              type="checkbox"
              id="contact"
              @change="handleContactMeChange"
              required
              style="
                margin-right: 10px;
                width: auto;
                height: auto;
                padding: 0;
                font-size: 17px;
                border: 1px solid #aaaaaa;
                border-radius: 5px;
                transform: scale(1.4);
              "
            />
            <label for="contact" style="font-size: 18px"
              >I would like a bank representative to contact me to discuss
              arranging a Security-Based Loan for my client *</label
            >
          </p>

          <!-- Security Check -->
          <CaptchaModal
            :open="captchaModalOpen"
            @verified="onCaptchaVerified"
            @unverified="onCaptchaUnverified"
            @close="onCaptchaModalClose"
          />

          <p
            v-if="submissionError"
            class="submission-error"
            role="alert"
            aria-live="assertive"
          >
            {{ submissionError }}
          </p>

          <!-- Navigation Buttons -->
          <div style="display: flex; justify-content: flex-end">
            <button
              type="button"
              aria-describedby="submit-help"
              @click="submitForm()"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
    <div class="callout">
      <LearningCards />
    </div>
  </div>
  <Footer />
</template>
<script setup>
import Logo from "./components/Logo";
import Footer from "./components/Footer";
import LoadingScreen from "./components/LoadingScreen";
import LearningCards from "./components/LearningCards.vue";
import Callout from "./components/Callout.vue";
import CaptchaModal from "./components/CaptchaModal.vue";
</script>

<script>
import { getCaptchaToken, isCaptchaConfigured } from "./services/captcha";

export default {
  components: {
    Logo,
    Footer,
    LoadingScreen,
    Callout,
    CaptchaModal,
  },
  data() {
    return {
      loading: false,
      captchaVerified: false,
      captchaModalOpen: false,
      awaitingCaptcha: false,
      submissionError: "",
      loadingMessages: [
        "Hang tight — we're connecting you with our partner banks...",
      ],
      contactMeChecked: false,
      form: {
        advisorName: "",
        firm: "",
        email: "",
        repCode: "",
        contactNumber: "",
        loanAmount: "",
        custodian: "",
        state: "",
        message: "",
        source: "",
      },
      validation: {
        advisorName: true,
        firm: true,
        email: true,
        contactNumber: true,
        loanAmount: true,
      },
      states: [
        "AL",
        "AK",
        "AZ",
        "AR",
        "CA",
        "CO",
        "CT",
        "DC",
        "DE",
        "FL",
        "GA",
        "HI",
        "ID",
        "IL",
        "IN",
        "IA",
        "KS",
        "KY",
        "LA",
        "ME",
        "MD",
        "MA",
        "MI",
        "MN",
        "MS",
        "MO",
        "MT",
        "NE",
        "NV",
        "NH",
        "NJ",
        "NM",
        "NY",
        "NC",
        "ND",
        "OH",
        "OK",
        "OR",
        "PA",
        "RI",
        "SC",
        "SD",
        "TN",
        "TX",
        "UT",
        "VT",
        "VA",
        "WA",
        "WV",
        "WI",
        "WY",
      ],
    };
  },
  computed: {
    apiUrl() {
      return `${process.env.VUE_APP_API_ENDPOINT}submit-request`;
    },
    partnerBanksAssetsDomain() {
      return process.env.VUE_APP_PARTNER_BANKS_ASSETS_DOMAIN;
    },
  },
  mounted() {
    this.form.source = this.$route.query.source || "UI";
  },
  methods: {
    handleContactMeChange() {
      if (this.contactMeChecked) {
        this.form.message =
          "I would like a bank representative to contact me to discuss arranging a Security-Based Loan for my client";
      } else {
        this.form.message = "";
      }
    },

    validateField(field) {
      const value = this.form[field];
      const skipFields = ["repCode", "custodian", "state"];

      if (skipFields.includes(field)) {
        return true; // skip validation for optional fields
      } else if (field === "email") {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return value.trim() !== "" && emailPattern.test(value);
      } else if (field === "contactMe") {
        return value === true;
      } else {
        return !!value;
      }
    },

    validateForm() {
      let isValid = true;

      Object.keys(this.form).forEach((field) => {
        const fieldIsValid = this.validateField(field);
        this.validation[field] = fieldIsValid;
        if (!fieldIsValid) isValid = false;
      });

      return isValid;
    },

    clearValidation(field) {
      if (this.validation[field] !== undefined && this.validateField(field)) {
        this.validation[field] = true;
      }
    },
    onCaptchaVerified() {
      this.captchaVerified = true;
      this.submissionError = "";
      this.captchaModalOpen = false;

      if (this.awaitingCaptcha) {
        this.awaitingCaptcha = false;
        this.sendReferral();
      }
    },

    onCaptchaUnverified() {
      this.captchaVerified = false;
    },

    onCaptchaModalClose() {
      this.captchaModalOpen = false;
      this.awaitingCaptcha = false;
    },

    async submitForm() {
      if (!this.validateForm()) {
        return;
      }

      this.submissionError = "";

      // The security check is only asked for at the point of submission, and
      // only while no valid token from a previous attempt is held.
      if (!this.captchaVerified && isCaptchaConfigured()) {
        this.awaitingCaptcha = true;
        this.captchaModalOpen = true;
        return;
      }

      await this.sendReferral();
    },

    async sendReferral() {
      this.loading = true;

      const headers = { "Content-Type": "application/json" };
      const captchaToken = await getCaptchaToken();
      if (captchaToken) {
        headers["x-aws-waf-token"] = captchaToken;
      }

      try {
        const res = await this.$http.post(this.apiUrl, this.form, { headers });
        this.$router.push({
          path: "/confirmation",
          state: {
            success: true,
            headerMessage: "Your Request has been Submitted Successfully",
            subHeaderMessage1: `Request ID: <strong>${res.data?.ReferenceNumber}</strong>`,
            subHeaderMessage2: `A confirmation email has been sent to: <strong>${this.form.email}</strong>`,
            details:
              `<p>If your request is successful, you will receive a loan offer from one or more of our partner banks.</p>` +
              `<p>If you have any queries, please contact:</br><strong>wealthlendingnetwork@broadridge.com</strong></p>`,
          },
        });
      } catch (error) {
        if (this.isCaptchaRejection(error)) {
          // Keep the advisor on the form so the security check can be
          // completed again without re-entering their details.
          this.submissionError =
            "We could not confirm the security check. Please complete it " +
            "again to send your referral.";
          this.captchaVerified = false;
          // Re-opening the modal mounts a fresh challenge widget.
          this.awaitingCaptcha = true;
          this.captchaModalOpen = true;
          return;
        }

        this.$router.push({
          path: "/confirmation",
          state: { success: false },
        });
      } finally {
        this.loading = false;
      }
    },

    isCaptchaRejection(error) {
      // AWS WAF returns 405 for a challenged request; the referral API
      // returns 403 when server side verification fails.
      const status = error?.response?.status;
      return status === 403 || status === 405;
    },
  },
};
</script>

<style scoped>
/* For Webkit browsers (Chrome, Safari, Edge, etc.) */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* For Firefox */
input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}

* {
  box-sizing: border-box;
}

#form {
  display: flex;
  flex-direction: column;
}

input,
select {
  padding: 10px;
  width: 100%;
  font-size: 17px;
  border: 1px solid #aaaaaa;
  border-radius: 5px;
}

input.invalid,
select.invalid {
  background-color: #ffdddd;
}

input::placeholder,
select.placeholder,
select option:disabled {
  color: #757575;
}

select option {
  color: #000;
}

button[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
}

.submission-error {
  color: #a4262c;
  font-size: 17px;
}

.submit-help {
  font-size: 16px;
  color: #333;
}
</style>
